<ng-container *ngIf="originalIndicatorData && originalIndicatorData.length">

  <!-- Columns that hold the same value on every row. They say what the extract is
       of, rather than anything that varies within it, so they are stated once here
       instead of repeated down every row of the table. -->
  <dl class="xt-fixed" *ngIf="constantColumns.length && !showAllColumnsOn">
    <div *ngFor="let column of constantColumns; trackBy: trackByIndex">
      <dt>{{ column.header }}</dt>
      <dd [title]="column.value">{{ column.value }}</dd>
    </div>
  </dl>

  <div *ngIf="tableInfo" class="mb-3 text-muted xt-info">
    <i class="fal fa-info-circle me-1"></i>{{ tableInfo }}
  </div>

  <div class="xt-toolbar d-print-none">

    <!-- Filter. Fields on the left, that field's values on the right, the same
         shape as the area picker in the blue selector bar. -->
    <div ngbDropdown #filterDropdown="ngbDropdown" display="dynamic" placement="bottom-start"
      [autoClose]="'outside'" (openChange)="onFilterOpenChange($event)">
      <button type="button" class="xt-trigger" ngbDropdownToggle>
        <i class="fas fa-filter xt-trigger__icon" aria-hidden="true"></i>Filter
        <span class="xt-count" *ngIf="selectedOptions.length">1</span>
      </button>

      <div ngbDropdownMenu class="xt-panel xt-filter" [class.xt-filter--step-values]="isFilterGroupOpened">
        <div class="xt-panel__head">
          <span class="xt-panel__title">Filter rows</span>
          <button type="button" class="xt-panel__action" *ngIf="selectedOptions.length" (click)="removeFilterOptions()">
            Clear filter
          </button>
        </div>

        <div class="xt-filter__cols">

          <div class="xt-filter__col xt-filter__col--fields" role="group" aria-label="Fields">
            <button type="button" class="xt-filter__field" *ngFor="let filter of tableFilters; trackBy: trackByIndex"
              [class.xt-filter__field--active]="selectedFilterGroup === filter"
              [attr.aria-expanded]="selectedFilterGroup === filter"
              (click)="showFilterGroupOptions(filter)">
              <span class="xt-filter__field-label">{{ filter }}</span>
              <i class="fas fa-check xt-filter__field-on" *ngIf="selectedFilterGroups.includes(filter)" aria-hidden="true"></i>
              <i class="fas fa-chevron-right xt-filter__caret" aria-hidden="true"></i>
            </button>
          </div>

          <div class="xt-filter__col xt-filter__col--values" role="group"
            [attr.aria-label]="selectedFilterGroup ? selectedFilterGroup + ' values' : 'Values'">

            <button type="button" class="xt-filter__back" *ngIf="isFilterGroupOpened" (click)="backToFilterFields()">
              <i class="fas fa-chevron-left me-2" aria-hidden="true"></i>{{ selectedFilterGroup }}
            </button>

            <div class="xt-panel__search" *ngIf="showOptionSearch">
              <i class="fas fa-search xt-panel__search-icon" aria-hidden="true"></i>
              <label class="visually-hidden" [attr.for]="optionSearchId">Search {{ selectedFilterGroup }} values</label>
              <input [id]="optionSearchId" type="text" class="xt-panel__search-input" autocomplete="off"
                [placeholder]="'Search ' + selectedFilterGroup + '...'"
                [value]="optionSearch" (input)="onOptionSearchChange($any($event.target).value)" />
            </div>

            <p class="xt-panel__empty" *ngIf="!isFilterGroupOpened">Choose a field on the left to filter by.</p>
            <p class="xt-panel__empty" *ngIf="isFilterGroupOpened && !visibleFilterOptions.length">
              No values match that search.
            </p>

            <button type="button" class="xt-panel__item" *ngFor="let option of visibleFilterOptions; trackBy: trackByIndex"
              [class.xt-panel__item--on]="tickOptionCheckBox(option)"
              [attr.aria-pressed]="tickOptionCheckBox(option)"
              (click)="onFilterGroupOptionSelect(option)">
              <i class="far fa-lg me-2" aria-hidden="true"
                [class.fa-dot-circle]="tickOptionCheckBox(option)"
                [class.fa-circle]="!tickOptionCheckBox(option)"></i>
              <span class="xt-panel__item-label">{{ option }}</span>
            </button>

          </div>
        </div>
      </div>
    </div>

    <!-- Columns. An extract is nearly twenty columns wide, and several of them
         carry the same value on every row or nothing at all, so the table starts
         on the ones that vary and the rest can be brought back from here. -->
    <div ngbDropdown display="dynamic" placement="bottom-start" [autoClose]="'outside'">
      <button type="button" class="xt-trigger" ngbDropdownToggle>
        <i class="fas fa-columns xt-trigger__icon" aria-hidden="true"></i>Columns
        <span class="xt-count">{{ visibleHeaders.length }}/{{ orderedHeaders.length }}</span>
      </button>
      <div ngbDropdownMenu class="xt-panel xt-columns">
        <div class="xt-panel__head">
          <span class="xt-panel__title">Columns</span>
          <button type="button" class="xt-panel__action" (click)="showAllColumns()">Show all</button>
          <button type="button" class="xt-panel__action" (click)="resetColumns()">Reset</button>
        </div>
        <div class="xt-panel__list">
          <button type="button" class="xt-panel__item" *ngFor="let header of orderedHeaders; trackBy: trackByIndex"
            [class.xt-panel__item--on]="isColumnVisible(header)"
            [attr.aria-pressed]="isColumnVisible(header)"
            (click)="toggleColumn(header)">
            <i class="far fa-lg me-2" aria-hidden="true"
              [class.fa-check-square]="isColumnVisible(header)"
              [class.fa-square]="!isColumnVisible(header)"></i>
            <span class="xt-panel__item-label">{{ header }}</span>
            <span class="xt-tag" *ngIf="isEmptyColumn(header)">empty</span>
            <span class="xt-tag" *ngIf="isConstantColumn(header)">same on every row</span>
          </button>
        </div>
      </div>
    </div>

    <!-- What is currently filtered, as a removable chip. -->
    <span class="xt-activefilter" *ngIf="activeFilterLabel">
      <span class="xt-activefilter__text">{{ activeFilterLabel }}</span>
      <button type="button" class="xt-activefilter__remove" (click)="removeFilterOptions()"
        [attr.aria-label]="'Remove the filter on ' + activeFilterLabel">
        <i class="fas fa-times" aria-hidden="true"></i>
      </button>
    </span>

    <button *ngIf="systemLevelSelected?.SystemLevelID" type="button" class="btn btn-sm btn-primary xt-toolbar__download"
      (click)="exportTable('XLSX')">
      <i class="far fa-file-excel me-2" aria-hidden="true"></i>Download this extract
    </button>

  </div>

  <div class="table-responsive bg-white small mt-3 xt-scroll">
    <table class="table table-sm table-bordered xt-table">
      <thead>
        <tr class="bg-light">
          <th *ngFor="let header of visibleHeaders; trackBy: trackByIndex"
            [class.xt-num]="isNumericColumn(header)">{{ header }}</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let data of indicatorData | slice: pageOffset | slice: 0:rowsPerPage; trackBy: trackByIndex">
          <td *ngFor="let header of visibleHeaders; trackBy: trackByIndex"
            [class.xt-num]="isNumericColumn(header)"
            [title]="data[header]">{{ data[header] }}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Footer: where you are in the extract, how much of it to show at once, and
       the pager itself. -->
  <div class="xt-footer d-print-none">

    <p class="xt-footer__range" aria-live="polite">
      Showing <strong>{{ rangeStart | number }}</strong> to <strong>{{ rangeEnd | number }}</strong>
      of <strong>{{ totalRows | number }}</strong> rows
    </p>

    <div class="xt-footer__size" ngbDropdown display="dynamic" placement="top-start">
      <span class="xt-footer__size-label" id="xtRowsPerPageLabel">Rows per page</span>
      <button type="button" class="xt-trigger xt-trigger--plain" ngbDropdownToggle aria-labelledby="xtRowsPerPageLabel">
        {{ rowsPerPage }}
        <i class="fas fa-chevron-down xt-trigger__caret" aria-hidden="true"></i>
      </button>
      <div ngbDropdownMenu class="xt-panel xt-sizes">
        <button type="button" class="xt-panel__item" *ngFor="let size of rowsPerPageOptions; trackBy: trackByValue"
          [class.xt-panel__item--on]="size === rowsPerPage"
          (click)="setRowsPerPage(size)">
          <span class="xt-panel__item-label">{{ size }}</span>
          <i class="fas fa-check" *ngIf="size === rowsPerPage" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <ngb-pagination class="xt-pager"
        [collectionSize]="totalRows"
        [pageSize]="rowsPerPage"
        [(page)]="currentPage"
        (pageChange)="onPageChange(currentPage)"
        [maxSize]="5"
        [rotate]="true"
        [ellipses]="true"
        [boundaryLinks]="true"
    >
    </ngb-pagination>

  </div>
</ng-container>
